import Heading from '../../../components/Heading'
import { fabric } from 'fabric'
import Input from '../../../components/Input'
import TextAlignInput from '../../../components/TextAlignInput'
import FontCard from '../FontCard/FontCard'
import CircleColorPicker from '../../../components/CircleColorPicker'
import Card from '../../../components/Card'
import React, { useCallback, useMemo } from 'react'
import { StoredSpot } from '../../../types/Individualization'
import cx from 'classnames'
import styles from '../CustomizerPopup.module.css'
import { useTranslation } from 'react-i18next'
import { generateClipPath } from '../../../helpers/generateClipPath'
import { useCustomizer } from '../../../contexts/CustomizerContext'
import InputToolbar from "../common/InputToolbar/InputToolbar";

type AutoTextElementInputProps = {
    spot: StoredSpot
    uid: string
    onDelete?: (unique_id: string) => void
}

const AutoTextElementInput: React.FC<AutoTextElementInputProps> = ({
    spot,
    uid,
    onDelete,
}) => {
    const {
        userInputs,
        setUserInputs,
        getFabricByUniqueId,
        getClipPath,
        fonts,
        activeTemplate,
        uuids,
    } = useCustomizer()

    const filteredFonts = useMemo(() => {
        let tmpFonts = []
        if (spot.individual_font_family || spot.font_families.length === 0)
            return fonts
        else
            tmpFonts = fonts.filter((f) => {
                return spot.font_families.split(',').includes(f.id + '')
            })
        if (tmpFonts.length === 1 && tmpFonts[0].name && tmpFonts[0].path) {
            const fontFace = new FontFace(
                tmpFonts[0].name,
                `url(${tmpFonts[0].path})`
            )
            fontFace.load().then((f) => {
                document.fonts.add(f)
            })
        }
        return tmpFonts
    }, [fonts, spot])

    const calcMaxFontSize = (
        textElement: fabric.Text,
        clipPath: fabric.Rect
    ) => {
        if (
            clipPath.width &&
            clipPath.height &&
            textElement.width &&
            textElement.fontSize
        ) {
            const sizeWidth = Math.floor(
                (clipPath.width / (textElement.width + 1)) *
                    textElement.fontSize
            )
            const sizeHeight = Math.floor(
                (clipPath.height / textElement.textLines.length) * 0.75
            )

            return sizeWidth < sizeHeight ? sizeWidth : sizeHeight
        }

        return null
    }

    const updateSpot = useCallback(
        (
            storedSpot: StoredSpot,
            property: keyof fabric.Text,
            value: string,
            setActive: boolean = true
        ) => {
            const uuid = uuids.get(storedSpot.id)
            if (!uuid || !activeTemplate?.canvas) return

            const textElement = userInputs.get(uuid) as fabric.Text | null

            const clipPath = getClipPath(activeTemplate.canvas, storedSpot.id)

            if (!clipPath) return
            if (textElement) {
                textElement.set(property, value)

                const fontSize = calcMaxFontSize(textElement, clipPath)
                if (fontSize) {
                    textElement.set('fontSize', fontSize)
                }

                setUserInputs((prevData) => {
                    return new Map(prevData.set(uuid, textElement))
                })
                if (setActive)
                    activeTemplate.canvas.setActiveObject(textElement)
            } else {
                const text = new fabric.Text(value, {
                    hoverCursor: 'move',
                    top: clipPath.top,
                    left: clipPath.left,
                    angle: clipPath.angle,
                    fontSize: storedSpot.font_size,
                    fontFamily:
                        filteredFonts.length > 0
                            ? filteredFonts[0].name
                            : undefined,
                    fill: storedSpot.text_color ?? '#525252',
                    selectable: false,
                    evented: false,
                    centeredScaling: true,
                    centeredRotation: true,
                    originX: 'center',
                    originY: 'center',
                    data: {
                        unique_id: uuid,
                        spot_id: storedSpot.id,
                        template_id: storedSpot.template_id,
                        size: clipPath.data.size,
                        is_clip_path: false,
                    },
                    hasControls: false,
                    hasBorders: false,
                    hasRotatingPoint: false,
                })

                text.set('clipPath', generateClipPath(clipPath))
                const fontSize = calcMaxFontSize(text, clipPath)
                if (fontSize) {
                    text.set('fontSize', fontSize)
                }

                setUserInputs((prev) => new Map(prev.set(uuid, text)))
                activeTemplate.canvas.add(text)
                if (setActive) activeTemplate.canvas.setActiveObject(text)
            }

            activeTemplate.canvas.requestRenderAll()
        },
        [
            uuids,
            userInputs,
            activeTemplate,
            filteredFonts,
            getClipPath,
            setUserInputs,
        ]
    )

    const changeTextProperty = useCallback(
        (property: keyof fabric.Text, value: string) => {
            updateSpot(spot, property, value)
            activeTemplate?.spots
                .filter((s) => s.sync_id === spot.id)
                .forEach((s) => {
                    updateSpot(s, property, value, false)
                })
        },
        [activeTemplate, updateSpot, spot]
    )

    const { t } = useTranslation(['customizer'])

    return (
        <Card
            key={spot.id}
        >
            <Card.Header>
                <div>{spot.name}</div>
                <InputToolbar unique_id={uid} onDelete={onDelete} spot={spot}/>
            </Card.Header>
            <p dangerouslySetInnerHTML={{ __html: spot.hint ?? '' }} />
            <div>
                <Input
                    key={spot.id}
                    type={spot.multiline ? 'textarea' : 'text'}
                    placeholder={t('text_input')}
                    property={'text'}
                    value={
                        (getFabricByUniqueId(uid) as fabric.Text | null)
                            ?.text ?? ''
                    }
                    onChange={(property, value) =>
                        changeTextProperty('text', value)
                    }
                    classes={'zr-my-3'}
                    maxLength={spot.max_character}
                    withCount={true}
                />
                {(getFabricByUniqueId(uid) as fabric.Text | null) && (
                    <>
                        {spot.multiline && (
                            <div className={'zr-mb-3'}>
                                <span>{t('text_align')}</span>
                                <TextAlignInput
                                    property={'alignment'}
                                    onChange={(property, value) => {
                                        changeTextProperty('textAlign', value)
                                    }}
                                    value={
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.textAlign ?? ''
                                    }
                                />
                            </div>
                        )}
                        {filteredFonts.length > 1 && (
                            <div>
                                <span>
                                    {t('font_family')}:{' '}
                                    {
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.fontFamily
                                    }
                                </span>
                                <div
                                    className={cx(styles['text-font-wrapper'])}
                                >
                                    {filteredFonts
                                        .map((font) => (
                                            <FontCard
                                                chosen={
                                                    font.name ===
                                                    (
                                                        getFabricByUniqueId(
                                                            uid
                                                        ) as fabric.Text | null
                                                    )?.fontFamily
                                                }
                                                font={font}
                                                key={font.id}
                                                onClick={(font) => {
                                                    changeTextProperty(
                                                        'fontFamily',
                                                        font.name
                                                    )
                                                }}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                        {spot.individual_text_color && (
                            <div
                                className={cx(
                                    styles['text-font-color-wrapper']
                                )}
                            >
                                <CircleColorPicker
                                    label={t('font_color_sub')}
                                    property={'fill'}
                                    value={
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.fill?.toString() ?? '#000'
                                    }
                                    onChange={(_, value) => {
                                        changeTextProperty('fill', value)
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    )
}

export default AutoTextElementInput
